<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <ModuleShell
    :title="$t('views.selectedGoat.modules.health')"
    :icon="Stethoscope"
    :loading="loading"
    @click="navigateToHealth">
    <div v-if="!loading && !error">
      <!-- Latest Record -->
      <div v-if="latestRecord" class="text-sm">
        <p class="text-[var(--color-text)] opacity-60 mb-1">
          {{ $t('views.selectedGoat.modules.latestCheck') }}:
        </p>
        <p class="font-medium">{{ latestRecord.title }}</p>
        <p class="text-xs opacity-60 mt-1">
          {{ formatDate(latestRecord.measuredAt) }}
        </p>
      </div>

      <!-- No Data Message -->
      <div v-else class="text-sm text-[var(--color-text)] opacity-60 text-center py-4">
        {{ $t('views.selectedGoat.modules.noHealthData') }}
      </div>
    </div>

    <div v-if="error" class="text-sm text-red-600 dark:text-red-400">
      {{ error }}
    </div>
  </ModuleShell>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useHealthStore } from '@/stores/health.store.js'
import { storeToRefs } from 'pinia'
import ModuleShell from '@/components/ui/layout/ModuleShell.vue'
import { Stethoscope } from 'lucide-vue-next'
import { formatDate } from '@/utils/format/dateformatting'

const props = defineProps({
  goatId: {
    type: Number,
    required: true
  }
})

const router = useRouter()
const healthStore = useHealthStore()
const { loading, error } = storeToRefs(healthStore)

const allRecords = computed(() => healthStore.getByGoat(props.goatId))
const totalRecords = computed(() => allRecords.value.length)

const latestRecord = computed(() => {
  if (allRecords.value.length === 0) return null
  
  // Sort by measuredAt descending and get the latest
  return [...allRecords.value].sort((a, b) => {
    return new Date(b.measuredAt) - new Date(a.measuredAt)
  })[0]
})

function navigateToHealth() {
  router.push(`/goats/${props.goatId}/health`)
}

onMounted(async () => {
  await healthStore.fetchHealthRecordsByGoat(props.goatId)
})
</script>
